import { Image as ImageIcon } from 'lucide-react';
import type { MouseEventHandler } from 'react';
import type { ClientOfferReviewPhotoBlockLabels } from './client-offer-review-types';

/** The "no photo" placeholder — shown when a unit's rendition field is `null` (no image asset for its format). */
function Placeholder({ size, caption }: { size: number; caption?: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
      <ImageIcon width={size} height={size} strokeWidth={1.4} />
      {caption != null ? <span className="text-xs">{caption}</span> : null}
    </div>
  );
}

/**
 * Renders the real photo when `photoUrl` is present, falling back to
 * `Placeholder` otherwise. The image is decorative (the surrounding card
 * already states address/format/price as text), hence `alt=""`. Exported so
 * other photo surfaces for the same unit (e.g. the detail modal) reuse this
 * instead of re-implementing the same ternary.
 *
 * `photoUrl` is whichever rendition the caller already resolved (thumb or
 * display) — this component never chooses between them and never falls
 * back to an original URL itself. `loading` is opt-in per caller: pass
 * `"lazy"` for thumb-scale list/expanded-panel surfaces only — the
 * single-image detail-modal caller must leave it unset (media-photo-derivatives:
 * lazy-loading a modal-triggered single image delays the one photo the
 * user explicitly opened).
 */
export function PhotoOrPlaceholder({
  photoUrl,
  placeholderSize,
  placeholderCaption,
  loading,
}: {
  photoUrl: string | null;
  placeholderSize: number;
  placeholderCaption?: string;
  loading?: 'lazy' | 'eager';
}) {
  if (photoUrl == null) return <Placeholder size={placeholderSize} caption={placeholderCaption} />;
  return <img src={photoUrl} alt="" loading={loading} className="h-full w-full object-cover" />;
}

/**
 * Left-side photo column (large photo mode) — otodom.pl-style listing card:
 * a wider photo on the left, key params in the content column to its right
 * (see `ClientOfferReviewUnitCard`). Wider than `ClientOfferReviewSmallPhotoBlock`
 * but shares the same "left column, self-stretch height" placement so both
 * modes sit in the same spot in the card and only differ in size.
 */
export function ClientOfferReviewLargePhotoBlock({
  photoUrl,
  formatLine,
  lp,
  totalCount,
  sold,
  badge,
  onOpen,
  grayFilter,
  labels,
}: {
  photoUrl: string | null;
  formatLine: string;
  lp: string;
  totalCount: number;
  sold: boolean;
  badge: { label: string; className: string } | null;
  // Accepts the native click event (not just `() => void`) so callers can wrap it with
  // a `stopPropagation`-calling handler (CU-86cbc55rv: whole-card click, opt-out for
  // nested controls) without a type mismatch — every existing 0-arg caller stays valid.
  onOpen: MouseEventHandler<HTMLDivElement>;
  grayFilter: boolean;
  labels: ClientOfferReviewPhotoBlockLabels;
}) {
  return (
    <div
      onClick={onOpen}
      title={labels.zoomHint}
      className="relative flex w-56 shrink-0 cursor-zoom-in items-center justify-center self-stretch overflow-hidden bg-gradient-to-br from-muted to-border"
      style={{ filter: grayFilter ? 'grayscale(1) opacity(.5)' : 'none' }}
    >
      <PhotoOrPlaceholder
        photoUrl={photoUrl}
        placeholderSize={30}
        placeholderCaption={labels.placeholderCaptionLarge}
        loading="lazy"
      />
      <span className="absolute left-2 top-2 rounded-md bg-card/90 px-2 py-0.5 text-2xs font-medium text-foreground">
        {formatLine}
      </span>
      <span className="absolute bottom-2 right-2 rounded-md bg-black/55 px-1.5 py-0.5 font-mono text-2xs text-white">
        {lp}/{totalCount}
      </span>
      {sold ? (
        <span className="absolute right-2 top-2 rounded-md bg-status-sold px-2 py-0.5 text-2xs font-bold uppercase tracking-wide text-white">
          {labels.soldLabel}
        </span>
      ) : null}
      {badge ? (
        <span
          className={`absolute bottom-2 left-2 rounded-full px-2 py-0.5 text-2xs font-semibold ${badge.className}`}
        >
          {badge.label}
        </span>
      ) : null}
    </div>
  );
}

/** 112px-wide side thumbnail (small photo mode). */
export function ClientOfferReviewSmallPhotoBlock({
  photoUrl,
  sold,
  onOpen,
  grayFilter,
  labels,
}: {
  photoUrl: string | null;
  sold: boolean;
  /** See `ClientOfferReviewLargePhotoBlock`'s `onOpen` doc comment. */
  onOpen: MouseEventHandler<HTMLDivElement>;
  grayFilter: boolean;
  labels: ClientOfferReviewPhotoBlockLabels;
}) {
  return (
    <div
      onClick={onOpen}
      title={labels.zoomHint}
      className="relative flex w-28 shrink-0 cursor-zoom-in items-center justify-center self-stretch overflow-hidden bg-gradient-to-br from-muted to-border"
      style={{ filter: grayFilter ? 'grayscale(1) opacity(.5)' : 'none' }}
    >
      <PhotoOrPlaceholder photoUrl={photoUrl} placeholderSize={20} loading="lazy" />
      {sold ? (
        <span className="absolute inset-x-0 top-1.5 flex justify-center">
          <span
            className="rounded bg-status-sold px-2 py-px text-2xs font-bold uppercase tracking-wide text-white"
            style={{ transform: 'rotate(-10deg)' }}
          >
            {labels.soldLabel}
          </span>
        </span>
      ) : null}
    </div>
  );
}

/** 120px photo block inside the expanded panel (none photo mode + expanded). */
export function ClientOfferReviewExpandPhotoBlock({
  photoUrl,
  onOpen,
  labels,
}: {
  photoUrl: string | null;
  /** See `ClientOfferReviewLargePhotoBlock`'s `onOpen` doc comment. */
  onOpen: MouseEventHandler<HTMLDivElement>;
  labels: ClientOfferReviewPhotoBlockLabels;
}) {
  return (
    <div
      onClick={onOpen}
      title={labels.zoomHint}
      className="flex h-30 cursor-zoom-in items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-muted to-border"
    >
      <PhotoOrPlaceholder
        photoUrl={photoUrl}
        placeholderSize={18}
        placeholderCaption={labels.placeholderCaptionSmall}
        loading="lazy"
      />
    </div>
  );
}
